import { type CaptureFileJSONObject, CaptureRole, MarkerType, type Point } from '@ikegps/core-api';
import { MultiPoint, type PointCapture, Vector } from '@ikegps/core-measurement';
import { CollectionModelDrawableMarker } from '../drawable/marker.drawable';
import type { CollectionModelDrawableState } from '../drawable/drawable.state';
import { CollectionModelFieldMidSpan } from '../fields/midspan.field';

/** Why the derivation did (or did not) place a base, for the caller's logging. */
export type MidSpanBaseOutcome =
	/** A locked base marker was derived and added to the span capture. */
	| { status: 'created' }
	/**
	 * The span capture already has a base — user placed, or derived by an earlier run. Idempotent no-op.
	 *
	 * `locked` reports whether that base is still the tool's. A derived base that comes back **unlocked** means
	 * something round-tripped the flag away — a client that does not serialize `locked` reconstructing the
	 * measurement — and the derived reference plane is now user-movable. Callers should log it; the server
	 * deliberately does not re-lock, because it cannot tell that case apart from a base the user placed.
	 */
	| { status: 'alreadyHasBase'; locked: boolean }
	/** Only one capture, so there is nothing to derive from. Migrated Mid Span IKE Photos look like this. */
	| { status: 'noGroundReference' }
	/**
	 * The pair is not tagged as a dedicated-tool capture pair, so the field is left as an ordinary IKE Photo
	 * for the user to mark up. A legacy field retrofitted in place can hold two unrelated photos; inventing a
	 * locked measurement from their order would assert a height nobody took.
	 */
	| { status: 'notTagged' }
	/** A capture or its calibration has not loaded yet. Safe to retry once loading settles. */
	| { status: 'notReady' };

/**
 * Hand a tool-derived base back to the user.
 *
 * The MidSpan tool locks the base it derives so the reference plane cannot move out from under the
 * measurement, and that lock only makes sense while the tool owns the field. When ownership ends — the field
 * is migrated to a plain IKE Photo, or the department loses the entitlement — the base has to become an
 * ordinary user-owned one, or the user is left with a base they cannot move, offset or delete, recoverable
 * only by clearing every annotation on the photo.
 *
 * The base itself stays: it is geometrically correct, and the user's own markers already measure against it.
 * Releasing is one-way — {@link deriveMidSpanBase} never re-locks, because it cannot tell a released base
 * apart from one the user placed by hand.
 *
 * @returns whether a locked base was released, so callers can log it.
 */
export function releaseDerivedBase(drawableState: CollectionModelDrawableState): boolean {
	const base = drawableState.base;
	if (base == null || !base.locked) {
		return false;
	}

	base.locked = false;
	return true;
}

/** Where the laser struck, which for an IKE photo is the centre of the frame. */
function laserStrikePoint(capture: CaptureFileJSONObject): Vector {
	return new Vector(capture.resolution.width / 2, capture.resolution.height / 2);
}

/**
 * A type guard rather than a boolean: `PointToPoint` requires a non-optional `distance`, so narrowing here is
 * what lets `midSpanHeightInMeters` take a measurable capture without a cast.
 */
function hasGeometryInputs(capture: CaptureFileJSONObject | null): capture is CaptureFileJSONObject & PointCapture {
	return capture != null && capture.distance != null && capture.orientation != null && capture.calibrationid != null;
}

/**
 * Vertical distance in metres from the ground shot's strike point up to the span shot's strike point.
 *
 * Both captures are taken from the same device position, so a plain point-to-point between them is the wire
 * height. This is the same calculation the `spanheight` height provider uses.
 */
export function midSpanHeightInMeters(
	span: CaptureFileJSONObject & PointCapture,
	ground: CaptureFileJSONObject & PointCapture
): number {
	return Math.abs(MultiPoint.PointToPoint(span, ground).vertical);
}

/**
 * Derive the locked base marker for a MidSpan capture.
 *
 * The span photo has no height stick in it, so its reference plane has to be computed: take the laser strike
 * point, and step down by however far the ground shot says the ground is. `movePoint` treats **positive** y
 * as downward — the same convention `CollectionModelDrawableMarker.calculateM2Px` relies on with a negated
 * value to step *up* from a base.
 *
 * Idempotent, and safe to call before captures have loaded (returns `notReady`).
 */
export function deriveMidSpanBase(field: CollectionModelFieldMidSpan): MidSpanBaseOutcome {
	const span = field.spanCapture;
	const ground = field.groundCapture;

	if (span == null || ground == null) {
		return { status: 'noGroundReference' };
	}

	if (!hasGeometryInputs(span) || !hasGeometryInputs(ground)) {
		return { status: 'notReady' };
	}

	// Derivation trusts explicit tags only. Role inference and capture order decide which photo the map dot
	// and measurement land on, but a derived base is a locked measurement the user cannot move — that must
	// only come from a pair the dedicated tool actually captured as a pair.
	if (span.raw?.role !== CaptureRole.MIDSPAN_SPAN || ground.raw?.role !== CaptureRole.MIDSPAN_GROUND) {
		return { status: 'notTagged' };
	}

	const collection = field.collection;
	const drawableState = collection.getDrawableState(span.objectId);
	const existingBase = drawableState.base;
	if (existingBase != null) {
		return { status: 'alreadyHasBase', locked: existingBase.locked };
	}

	const heightInMeters = midSpanHeightInMeters(span, ground);
	const strike = laserStrikePoint(span);
	const basePoint = collection.state.getGeometry(span.objectId).movePoint(strike, new Vector(0, heightInMeters));

	// The ground routinely projects below the frame (crews frame the wire, not the ground). The base is
	// placed at the true computed position anyway — off-frame markers are representable and measure
	// correctly (a HEIGHT field's manual input already moves markers off-frame via `moveNoBounds`); clamping
	// to the image edge would read as a real measurement and be wrong. An off-frame base is outside the
	// rendered viewBox, so it measures without being visible, and its label pins to the frame edge (UFO-1372).
	// Base labels sit hard against the left edge, matching where the base tool places them.
	const labelPoint: Point = { x: 0, y: basePoint.y };
	const { marker } = CollectionModelDrawableMarker.createWithLabel(
		drawableState,
		{ x: basePoint.x, y: basePoint.y },
		labelPoint,
		undefined,
		MarkerType.BASE
	);
	marker.locked = true;

	return { status: 'created' };
}

export type MidSpanMeasurementOutcome =
	/** A wire marker was placed at the laser strike point. */
	| { status: 'created' }
	/**
	 * Nothing to add: either the base predates this run, or the photo already carries ordinary markers. The wire
	 * marker is placed exactly once, in the run that derives the base, so a user who deletes it keeps it deleted.
	 */
	| { status: 'alreadyMarked' }
	/**
	 * No reference plane, so a marker would measure nothing. The base has to exist before a marker is worth
	 * placing.
	 */
	| { status: 'noBase' }
	/**
	 * The span capture's base was placed by hand, not derived by this tool, so the wire is the user's to
	 * measure. A migrated Mid Span IKE Photo the crew already referenced with a height stick looks like this.
	 */
	| { status: 'userPlacedBase' };

export interface MidSpanSetupOutcome {
	base: MidSpanBaseOutcome;
	measurement: MidSpanMeasurementOutcome;
}

/**
 * Set up what the dedicated MidSpan tool derives: the locked base, and a marker on the laser strike point
 * where the wire was hit.
 *
 * The marker is deliberately **not linked to any field**. On a real PLA form the mid span height fields live
 * inside the nested Wire subform (`spanWireMidSpanHeight`), one per wire, so there is no unambiguous target —
 * the laser hit one specific wire, not necessarily the first. The user links the marker to the wire they
 * measured using the existing link-to-photo control, exactly as they do today, and the height still lands in
 * a HEIGHT field so SpidaCalc and DDS exports are unaffected.
 *
 * Idempotent — safe to run on every save.
 */
export function setUpMidSpanMeasurement(field: CollectionModelFieldMidSpan): MidSpanSetupOutcome {
	const base = deriveMidSpanBase(field);

	const span = field.spanCapture;
	if (span == null) {
		return { base, measurement: { status: 'noBase' } };
	}

	const drawableState = field.collection.getDrawableState(span.objectId);
	const spanBase = drawableState.base;
	if (spanBase == null) {
		return { base, measurement: { status: 'noBase' } };
	}

	// Only measure against a base this tool derived — `locked` is set nowhere else. A hand-placed base means the
	// crew referenced the photo themselves (every migrated single-capture field is like this), and a centre-frame
	// marker there asserts a measurement nobody took.
	if (!spanBase.locked) {
		return { base, measurement: { status: 'userPlacedBase' } };
	}

	// Place the marker only in the run that derives the base. Once the base predates this run there is no way to
	// tell "never marked" from "the user deleted the marker", and re-deriving it on every save made the
	// annotation impossible to remove.
	if (base.status !== 'created') {
		return { base, measurement: { status: 'alreadyMarked' } };
	}

	// The user has already annotated this photo, so the derived base is all they needed — their own markers now
	// measure against it. An extra unlinked marker on the strike point would just be noise.
	const hasMarker = drawableState.values().some((drawable) => CollectionModelDrawableMarker.isNone(drawable));
	if (hasMarker) {
		return { base, measurement: { status: 'alreadyMarked' } };
	}

	const strike = laserStrikePoint(span);
	CollectionModelDrawableMarker.createWithLabel(
		drawableState,
		{ x: strike.x, y: strike.y },
		{ x: span.resolution.width * 0.75, y: strike.y }
	);

	return { base, measurement: { status: 'created' } };
}
